import { useState } from "react";
import { Button, Divider, Image, cn } from "@heroui/react";
import { RotateCw, ExternalLink, Smartphone } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { getFileType } from "@/utils/file-utils";
import { isMobile } from "react-device-detect";
import { t } from "@lingui/core/macro";

interface FilePreviewProps {
  fileUrl: string;
  fileName: string;
  className?: string;
}

export const FilePreview = ({
  fileUrl,
  fileName,
  className,
}: FilePreviewProps) => {
  const fileType = getFileType(fileName);

  if (fileType === "image") {
    return (
      <ImagePreview
        fileUrl={fileUrl}
        fileName={fileName}
        className={className}
      />
    );
  }

  if (fileType === "pdf") {
    return (
      <PDFPreview fileUrl={fileUrl} fileName={fileName} className={className} />
    );
  }

  return (
    <div
      className={cn(
        "flex h-96 items-center justify-center text-gray-500",
        className,
      )}
    >
      <p>
        <Trans>Predogled ni na voljo za to vrsto datoteke</Trans>
      </p>
    </div>
  );
};

interface ImagePreviewProps {
  fileUrl: string;
  fileName: string;
  className?: string;
}

const ImagePreview = ({ fileUrl, fileName, className }: ImagePreviewProps) => {
  const [hasError, setHasError] = useState(false);
  const [rotation, setRotation] = useState(0);

  const handleImageLoad = () => {
    setHasError(false);
  };

  const handleImageError = () => {
    setHasError(true);
  };

  const rotate = () => setRotation((prev) => (prev + 90) % 360);
  const resetView = () => {
    setRotation(0);
  };

  if (hasError) {
    return (
      <div
        className={cn(
          "flex h-96 items-center justify-center text-gray-500",
          className,
        )}
      >
        <p>
          <Trans>Napaka pri nalaganju slike</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="flat"
            aria-label={t`Zavrti sliko`}
            onPress={rotate}
          >
            <RotateCw size={16} />
          </Button>
        </div>
        <Button size="sm" variant="flat" onPress={resetView}>
          <Trans>Ponastavi</Trans>
        </Button>
      </div>

      <Divider className="my-4" />

      {/* Image */}
      <div className="flex-1 overflow-auto">
        <div>
          <div
            className="inline-block"
            style={{
              // Create a wrapper that expands with the scaled image
              transform: `rotate(${rotation}deg)`,
              transformOrigin: "center",
            }}
          >
            <Image
              src={fileUrl}
              alt={fileName}
              className={cn(
                "block max-h-[50vh] max-w-full object-contain transition-transform",
              )}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface PDFPreviewProps {
  fileUrl: string;
  fileName: string;
  className?: string;
}

const PDFPreview = ({ fileUrl, fileName, className }: PDFPreviewProps) => {
  // On mobile, provide a different experience due to poor native PDF support
  if (isMobile) {
    return (
      <div className={cn("flex h-full flex-col", className)}>
        {/* Mobile PDF viewing is often problematic, so provide alternatives */}
        <div className="mb-4 rounded-lg bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <Smartphone className="mt-1 h-5 w-5 text-blue-600" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-blue-900">
                <Trans>Mobilni predogled</Trans>
              </h4>
              <p className="mt-1 text-sm text-blue-700">
                <Trans>
                  Za boljšo izkušnjo na mobilnih napravah priporočamo odpiranje
                  PDF-ja v zunanji aplikaciji.
                </Trans>
              </p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              as="a"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              size="sm"
              startContent={<ExternalLink size={16} />}
            >
              <Trans>Odpri v PDF aplikaciji</Trans>
            </Button>
          </div>
        </div>

        {/* Still try to show PDF inline, but with mobile-optimized iframe */}
        <div className="flex-1">
          <iframe
            src={`${fileUrl}#view=FitH&pagemode=none&toolbar=1`}
            className="h-full w-full rounded-lg border-0"
            title={fileName}
            loading="lazy"
            referrerPolicy="no-referrer"
            sandbox="allow-same-origin allow-scripts allow-popups allow-downloads"
          />
        </div>
      </div>
    );
  }

  // Desktop experience with object tag
  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="flex-1">
        <object
          data={`${fileUrl}#view=FitH&pagemode=none&toolbar=1`}
          type="application/pdf"
          className="h-full w-full rounded-lg"
          aria-label={fileName}
        >
          {/* Fallback for browsers that don't support PDF viewing */}
          <div className="flex h-full items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-2">
                <Trans>PDF ni mogoče prikazati v brskalniku</Trans>
              </p>
              <Button
                as="a"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                color="primary"
                size="sm"
                startContent={<ExternalLink size={16} />}
              >
                <Trans>Odpri PDF</Trans>
              </Button>
            </div>
          </div>
        </object>
      </div>
    </div>
  );
};
